import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  Phone,
  MapPinLine,
  CurrencyCircleDollar,
  Clock,
  Cardholder,
  CreditCard,
  Bell,
  Eye,
  FileText,
  Archive,
  PaperPlaneRight,
  Warning,
  HandFist,
  Handshake,
  PencilSimple,
  Envelope,
  AddressBook,
  Graph,
  Buildings,
  X,
  Lightning,
  CaretDown,
  CaretRight,
  Repeat,
} from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import {
  frequencyOptions,
  onOptionsMap,
  strategyOptions,
} from '../../../general/AppAutopayModal/AppAutopayModal';
import { twMerge } from 'tailwind-merge';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  Buyer,
  BuyerUserPermissions,
  DocumentsSummary,
  formatMoney,
  DEFAULT_AGING_RANGES,
  QuickbooksInvoice,
  QuickbooksInvoiceSchema,
  QuickbooksCreditMemo,
  QuickbooksCreditMemoSchema,
  QuickbooksJob,
  calculateAgingBucket,
  assignCreditsToAgingBuckets,
  isCreditMemoSelectable,
  BuyerContact,
  removeDuplicatesByProperty,
} from '@dill/dill-shared';
import {
  getUserCustomer,
  getMainBuyerForPreview,
  getSupplierSummary,
} from '../../../../redux/services/suppliersService';
import { buildSupplierPath, openDillToast } from '../../../../utils/helpers';
import CustomerInvoicesTable from '../CustomerInvoicesTable/CustomerInvoicesTable';
import { updateSupplierBuyer } from '../../../../redux/services/supplierBuyersService';
import { getSupplierSalesReps } from '../../../../redux/services/supplierAdditionalDetailsService';
import {
  DillButton,
  DillCheckbox,
  DillDropdown,
  DillToggle,
  DillSelect,
  DillModal,
} from '../../../general/Dill';
import SupplierCustomerPaymentMethodsModal from './SupplierCustomerPaymentMethodsModal/SupplierCustomerPaymentMethodsModal';
import {
  resetSelectInvoice,
  selectInvoice,
  unSelectInvoice,
  selectCreditMemo,
  unSelectCreditMemo,
} from '../../../../redux/globalSlices/invoiceSlice';
import {
  resetInvoiceIdsForDownload,
  selectInvoiceIdsForDownload,
  unSelectInvoiceIdsForDownload,
} from '../../../../redux/globalSlices/buyersSlice';
import AccountAccessModal from './AccountAccessModal/AccountAccessModal';
import CRMContactsModal from './CRMContactsModal/CRMContactsModal';
import { User } from 'react-feather';
import CreditCardSettingsModal from './CreditCardSettingsModal/CreditCardSettingsModal';
import SupplierCustomerEmailPreferencesModal from './SupplierCustomerEmailPreferencesModal/SupplierCustomerEmailPreferencesModal';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { enterPreviewMode } from '../../../../redux/globalSlices/previewSlice';
import CollectionActivityTimeline from '../../suppliers/CollectionActivityTimeline/CollectionActivityTimeline';
import { clearBuyerActivities } from '../../../../redux/globalSlices/collectionActivitySlice';
import { SupplierCustomerPaymentsTable } from './SupplierCustomerPaymentsTable/SupplierCustomerPaymentsTable';
import { SupplierCustomerPaymentRequests } from './SupplierCustomerPaymentRequests/SupplierCustomerPaymentRequests';
import SupplierCustomerDisputes from './SupplierCustomerDisputes/SupplierCustomerDisputes';
import SupplierCustomerPromiseToPays from './SupplierCustomerPromiseToPays/SupplierCustomerPromiseToPays';
import { SupplierCustomerPaymentPlan } from './SupplierCustomerPaymentPlan/SupplierCustomerPaymentPlan';
import { BuyerCollectionTriggerTargets } from '../../suppliers/CollectionTriggersAndSequences/BuyerCollectionTriggerTargets';
import { BuyerCollectionSequenceEnrollments } from '../../suppliers/CollectionTriggersAndSequences/BuyerCollectionSequenceEnrollments';
import { getSupplierCollectionSequences } from '../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../redux/services/collectionTriggerService';
import { APP_ENVIRONMENT } from '../../../../utils/constants';
import { SupplierCustomerDashboard } from './SupplierCustomerDashboard';

type MainTab =
  | 'INVOICES'
  | 'COLLECTIONS'
  | 'PAYMENTS'
  | 'PAYMENT_REQUESTS'
  | 'DISPUTES'
  | 'DASHBOARD'
  | 'PROMISE_TO_PAY'
  | 'PAYMENT_PLAN';
// Collection Workflows always shows on the Collections tab; Activity collapses into an
// expandable summary card above it instead of living behind its own sub-tab. Promise to Pay
// is back to being its own top-level tab (after Dashboard).

export const CustomerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { supplierUsers } = useAppSelector((state) => state.supplier);
  const { salesReps } = useAppSelector((state) => state.salesReps);
  const downloadIds = useAppSelector((state) => state.buyers.invoiceIdsForDownload);
  const selectedCreditMemosState = useAppSelector((state) => state.invoice.selectedCreditMemos);
  const { sequences: collectionSequences } = useAppSelector((state) => state.collectionSequences);
  const { triggers: collectionTriggers } = useAppSelector((state) => state.collectionTriggers);
  // Only worth showing the "Collection Sequences & Triggers" card at all if the supplier has
  // actually set at least one of either up - otherwise it's just an empty collapsed shell.
  const hasAnyCollectionSequencesOrTriggers =
    collectionSequences.length > 0 || collectionTriggers.length > 0;

  const [currentBuyerId, setCurrentBuyerId] = useState<string>('');
  const [isWorkflowsExpanded, setIsWorkflowsExpanded] = useState(false);
  const [updatingLienStatus, setUpdatingLienStatus] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState<Buyer | null>(null);
  const [documentsSummary, setDocumentsSummary] = useState<DocumentsSummary | null>(null);
  const [buyerInvoices, setBuyerInvoices] = useState<(QuickbooksInvoice | any)[]>([]);
  const [buyerCreditMemos, setBuyerCreditMemos] = useState<(QuickbooksCreditMemo | any)[]>([]);
  const [customerDetails, setCustomerDetails] = useState<{
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      firstName: string;
      lastName: string;
      userId: string;
      isPasswordSet?: boolean;
      isInvitedUser: boolean;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
      permissions?: BuyerUserPermissions;
    }[];
    documentsSummary: DocumentsSummary;
  } | null>(null);

  const [isSupplierCustomerPaymentMethodsModalOpen, setIsSupplierCustomerPaymentMethodsModalOpen] =
    useState(false);
  const [isPaymentSettingsModalOpen, setIsPaymentSettingsModalOpen] = useState(false);
  const [isEmailPreferencesModalOpen, setIsEmailPreferencesModalOpen] = useState(false);
  const [creditManagerModalOpen, setCreditManagerModalOpen] = useState(false);
  const [selectedCreditManagerId, setSelectedCreditManagerId] = useState<string>('');
  const [savingCreditManager, setSavingCreditManager] = useState(false);
  const [salesRepModalOpen, setSalesRepModalOpen] = useState(false);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState<string>('');
  const [savingSalesRep, setSavingSalesRep] = useState(false);
  const [isAccountAccessModalOpen, setIsAccountAccessModalOpen] = useState(false);
  const [isCRMContactsModalOpen, setIsCRMContactsModalOpen] = useState(false);
  // Dummy state — UI only, no backend wiring yet. Hidden in production.
  const [isCreditHoldDummy, setIsCreditHoldDummy] = useState(false);
  const fetchBuyer = async (buyerId: string) => {
    const result = await dispatch(
      getUserCustomer({ supplierId: currentSupplier?.id || '', buyerId })
    );
    if (result.type === 'supplier/getUserCustomer/fulfilled') {
      const payload = result.payload as any;
      if (payload.data) {
        setCustomerDetails(payload.data);
      }
      if (payload?.data?.buyer) {
        const buyer: Buyer = payload.data.buyer;
        setSelectedBuyer(buyer);
      }
      if (payload?.data?.documentsSummary) {
        const buyerSummary: DocumentsSummary = payload.data.documentsSummary;
        setDocumentsSummary(buyerSummary);
      }
    } else {
      openDillToast({ message: 'Failed to fetch buyer.', type: 'ERROR' });
    }
  };
  useEffect(() => {
    if (supplierUsers.length === 0) {
      dispatch(getSupplierSummary({ supplierId: currentSupplier?.id || '' }));
    }
  }, [user, currentSupplier?.id]);

  useEffect(() => {
    if (currentSupplier?.id && salesReps.length === 0) {
      dispatch(getSupplierSalesReps({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id]);

  useEffect(() => {
    if (selectedBuyer?.isBuyerOnCreditHold) {
      setIsCreditHoldDummy(selectedBuyer.isBuyerOnCreditHold);
    }
  }, [selectedBuyer]);

  // Fetched here (not just inside the child components) so we know up front whether the supplier
  // has any Collection Sequences/Triggers set up at all, and can hide the whole card if not. Only
  // fetches what isn't already in the store - avoids a redundant refetch on every buyer switch.
  useEffect(() => {
    if (!currentSupplier?.id) return;
    if (collectionSequences.length === 0) {
      dispatch(getSupplierCollectionSequences({ supplierId: currentSupplier.id }));
    }
    if (collectionTriggers.length === 0) {
      dispatch(getSupplierCollectionTriggers({ supplierId: currentSupplier.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSupplier?.id]);

  const handlePreviewBuyerSide = async () => {
    const supplierDomainId = currentSupplier?.domainId;

    if (!supplierDomainId) {
      openDillToast({
        message: 'Unable to preview buyer side. Missing supplier domain ID.',
        type: 'ERROR',
      });
      return;
    }

    try {
      const result = await dispatch(
        getMainBuyerForPreview({
          supplierId: currentSupplier?.id || '',
          buyerId: currentBuyerId,
        })
      );

      if (result.type === 'supplier/getMainBuyerForPreview/fulfilled') {
        const payload = result.payload as any;
        if (payload?.data) {
          dispatch(
            enterPreviewMode({
              mainBuyer: payload.data,
              returnPath: location.pathname,
              previewSupplierDomainId: supplierDomainId,
            })
          );
          navigate(`/companies/${payload.data.id}/invoices/${supplierDomainId}`);
        }
      } else {
        openDillToast({
          message: 'Unable to preview buyer side. No MainBuyer found for this customer.',
          type: 'ERROR',
        });
      }
    } catch (error) {
      openDillToast({
        message: 'Failed to fetch buyer preview data.',
        type: 'ERROR',
      });
    }
  };

  const handleInvoicesFetched = (invoices: (QuickbooksInvoice | any)[]) => {
    setBuyerInvoices(
      invoices.filter(
        (documentDetails) =>
          QuickbooksInvoiceSchema.safeParse(documentDetails).success &&
          documentDetails.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
          (documentDetails.dillPaymentStatus !== 'RECONCILIATION_PENDING' ||
            (documentDetails.balance && documentDetails.balance > 0))
      )
    );
    setBuyerCreditMemos(
      invoices
        .filter((doc) => QuickbooksCreditMemoSchema.safeParse(doc).success)
        .map((doc) => ({
          ...QuickbooksCreditMemoSchema.parse(doc),
          // Keep the raw dates. Parsing coerces them to Date, which drops the embedded offset and
          // lets the browser timezone shift a credit's aging bucket — buyerInvoices above is
          // stored unparsed, so credits must be too or the two disagree on the same timestamp.
          dueDate: doc.dueDate,
          createdAt: doc.createdAt,
        }))
    );
  };

  const handleAddBuyerContact = async (contact: Omit<BuyerContact, 'id'>) => {
    const newContact: BuyerContact = {
      ...contact,
      id: crypto.randomUUID(),
    };
    const existingContacts = selectedBuyer?.buyerContacts || [];
    const updatedContacts = [...existingContacts, newContact];
    const resp = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: currentBuyerId,
        supplierBuyerDetails: { buyerContacts: updatedContacts },
      })
    );
    if (resp.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      fetchBuyer(currentBuyerId);
    } else {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: 'Failed to add contact',
        })
      );
    }
  };

  const handleDeleteBuyerContact = async (contactId: string) => {
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Removing contact',
        subTitle: 'Removing contact from this customer...',
      })
    );
    const existingContacts = selectedBuyer?.buyerContacts || [];
    const updatedContacts = existingContacts.filter((c) => c.id !== contactId);
    const resp = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: currentBuyerId,
        supplierBuyerDetails: { buyerContacts: updatedContacts },
      })
    );
    if (resp.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'Contact Removed',
          subTitle: 'The contact has been removed from this customer',
        })
      );
      fetchBuyer(currentBuyerId);
    } else {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: 'Failed to remove contact',
        })
      );
    }
  };

  useEffect(() => {
    const pathlist = location.pathname.split('/');
    const buyerId = pathlist[pathlist.length - 1];
    setCurrentBuyerId(buyerId);
    // Clear previous buyer's collection activities before fetching new buyer
    dispatch(clearBuyerActivities());
    if (user && currentSupplier?.id) {
      fetchBuyer(buyerId);
    }
    dispatch(resetSelectInvoice());
    dispatch(resetInvoiceIdsForDownload());
  }, [location, user?.id, currentSupplier?.id]);

  const handleUpdateBuyerLienNotification = async (val: boolean, buyerId: string) => {
    setUpdatingLienStatus(buyerId);
    const resp = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: buyerId,
        supplierBuyerDetails: { isPrelienNotificationsDisabled: val },
      })
    );
    if (resp.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      await fetchBuyer(buyerId);
      openDillToast({
        message: `Lien-related notifications to your team about this customer is turned ${
          val ? 'off' : 'on'
        }`,
        type: 'SUCCESS',
      });
      setUpdatingLienStatus('');
    } else {
      setUpdatingLienStatus('');
      openDillToast({
        message: 'Failed to update customer for lien-related notifications',
        type: 'ERROR',
      });
    }
  };

  const handleSaveCreditManager = async () => {
    setSavingCreditManager(true);
    const resp = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: currentBuyerId,
        supplierBuyerDetails: { asignedCreditManagerId: selectedCreditManagerId || null } as any,
      })
    );
    setSavingCreditManager(false);
    if (resp.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      await fetchBuyer(currentBuyerId);
      setCreditManagerModalOpen(false);
      openDillToast({ message: 'Credit manager updated', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to update credit manager', type: 'ERROR' });
    }
  };

  const creditManagers = useMemo(
    () =>
      (supplierUsers ?? [])
        .filter((u) => u.roles.includes('SUB_SUPPLIER') || u.roles.includes('SUPPLIER'))
        .map((u) => ({
          id: u.id,
          firstName: u.firstName ?? '',
          lastName: u.lastName ?? '',
          email: u.email,
        })),
    [supplierUsers]
  );

  const currentCreditManager = useMemo(() => {
    if (!selectedBuyer?.asignedCreditManagerId) return null;
    return creditManagers.find((cm) => cm.id === selectedBuyer?.asignedCreditManagerId);
  }, [selectedBuyer?.asignedCreditManagerId, creditManagers]);

  const handleSaveSalesRep = async () => {
    setSavingSalesRep(true);
    const resp = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: currentBuyerId,
        supplierBuyerDetails: { salesRepId: selectedSalesRepId || null } as any,
      })
    );
    setSavingSalesRep(false);
    if (resp.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      await fetchBuyer(currentBuyerId);
      setSalesRepModalOpen(false);
      openDillToast({ message: 'Sales rep updated', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to update sales rep', type: 'ERROR' });
    }
  };

  const currentSalesRep = useMemo(() => {
    if (!selectedBuyer?.salesRepId) return null;
    return salesReps.find((sp) => sp.id === selectedBuyer?.salesRepId) ?? null;
  }, [selectedBuyer?.salesRepId, salesReps]);

  // Tab state - driven by URL query params
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);

  const activeMainTab = useMemo<MainTab>(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const validMainTabs = [
      'INVOICES',
      'COLLECTIONS',
      'PAYMENTS',
      'PAYMENT_REQUESTS',
      'DISPUTES',
      'DASHBOARD',
      'PROMISE_TO_PAY',
      'PAYMENT_PLAN',
    ] as const;
    if (tabParam && (validMainTabs as readonly string[]).includes(tabParam)) {
      return tabParam as MainTab;
    }
    if (params.get('transactionId')) {
      return 'PAYMENTS';
    }
    if (params.get('paymentRequestId')) {
      return 'PAYMENT_REQUESTS';
    }
    return 'DASHBOARD';
  }, [location.search]);

  const setActiveMainTab = (tab: MainTab) => {
    const params = new URLSearchParams(location.search);
    params.delete('transactionId');
    params.delete('paymentRequestId');
    if (tab === 'DASHBOARD') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const buyerInitial = useMemo(() => {
    if (selectedBuyer) {
      const selectedBuyerName = selectedBuyer?.name ?? '';
      const selectedBuyerNameArray = selectedBuyerName.split(' ');
      if (selectedBuyerNameArray.length > 1) {
        const letter1 = selectedBuyerNameArray[0].substring(0, 1);
        const letter2 = selectedBuyerNameArray[1].substring(0, 1);
        return `${letter1}${letter2}`.toUpperCase();
      } else {
        const letter1 = selectedBuyerNameArray[0].substring(0, 1);
        return `${letter1}`.toUpperCase();
      }
    }
    return '';
  }, [selectedBuyer]);

  const buyerAddress = useMemo(() => {
    if (selectedBuyer) {
      const selectedAddress = selectedBuyer?.address ? `${selectedBuyer?.address}, ` : '';
      const selectedCity = selectedBuyer?.city ? `${selectedBuyer?.city}, ` : '';
      const selectedState = selectedBuyer?.state ? `${selectedBuyer?.state} ` : '';
      const selectedZipCode = selectedBuyer?.zipCode ?? '';
      return `${selectedAddress}${selectedCity}${selectedState}${selectedZipCode}`;
    }
    return '';
  }, [selectedBuyer]);

  // Determine if using custom aging ranges
  const isUsingAgingRanges = useMemo(
    () =>
      (currentSupplier?.isUseAgingRanges &&
        currentSupplier?.agingRanges &&
        currentSupplier?.agingRanges.length > 0) ??
      false,
    [currentSupplier?.isUseAgingRanges, currentSupplier?.agingRanges, currentSupplier?.id]
  );

  const summaryDetails = useMemo(() => {
    if (documentsSummary) {
      const activeJobIds = selectedJobIds.filter((id) => id !== 'ALL');
      const isJobFiltered = activeJobIds.length > 0;

      // Get all open invoices for aging bucket calculation, filtered by job if active
      const allOpenInvoices = isJobFiltered
        ? (buyerInvoices || []).filter(
            (inv) => inv.quickBooksJob && activeJobIds.includes(inv.quickBooksJob.id)
          )
        : buyerInvoices || [];

      const openCreditMemos = isJobFiltered
        ? (buyerCreditMemos || []).filter(
            (cm) => cm.quickBooksJob && activeJobIds.includes(cm.quickBooksJob.id)
          )
        : buyerCreditMemos || [];

      // Sum of remaining credit for jobs that match the current job filter
      const jobCreditTotal = isJobFiltered
        ? openCreditMemos.reduce((sum, cm) => sum + (cm.remainingCredit ?? 0), 0)
        : 0;

      // Use date-only string comparison to avoid timezone/sub-day truncation issues.
      // moment.parseZone preserves the offset embedded in the due date string so the
      // calendar date is never shifted by the browser's local timezone.
      const todayStr = moment().format('YYYY-MM-DD');

      const getDueDateStr = (dueDate: string) => moment.parseZone(dueDate).format('YYYY-MM-DD');

      const getDaysPastDue = (dueDate: string) =>
        moment(todayStr).diff(moment(getDueDateStr(dueDate)), 'days');

      // Calculate CURRENT DUE invoices
      const currentDueInvoices = allOpenInvoices.filter((inv) => {
        if (!inv.dueDate) return false;
        return getDueDateStr(inv.dueDate) >= todayStr; // Due today or in the future
      });

      // === Pre-compute gross bucket amounts and apply credits oldest-first ===

      type BucketEntry = {
        order: number;
        invoices: (QuickbooksInvoice | any)[];
        creditMemos: QuickbooksCreditMemo[];
        adjustedAmount: number;
      };

      const sortedRanges = isUsingAgingRanges
        ? [
            ...(currentSupplier?.agingRanges && currentSupplier.agingRanges.length > 0
              ? currentSupplier.agingRanges
              : DEFAULT_AGING_RANGES),
          ].sort((a, b) => a.order - b.order)
        : null;

      const computedBuckets: BucketEntry[] = [];

      if (isUsingAgingRanges && sortedRanges) {
        for (const range of sortedRanges) {
          const bucketInvoices = allOpenInvoices.filter((inv) => {
            if (!inv.dueDate) return false;
            const daysPastDue = getDaysPastDue(inv.dueDate);
            if (daysPastDue <= 0) return false;
            return calculateAgingBucket(daysPastDue, sortedRanges) === range.order;
          });
          computedBuckets.push({
            order: range.order,
            invoices: bucketInvoices,
            creditMemos: [],
            adjustedAmount: bucketInvoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0),
          });
        }
      } else {
        const b0: (QuickbooksInvoice | any)[] = [];
        const b1: (QuickbooksInvoice | any)[] = [];
        const b2: (QuickbooksInvoice | any)[] = [];
        const b3: (QuickbooksInvoice | any)[] = [];
        for (const inv of allOpenInvoices) {
          if (!inv.dueDate) continue;
          const daysPastDue = getDaysPastDue(inv.dueDate);
          if (daysPastDue <= 0) continue;
          if (daysPastDue <= 30) b0.push(inv);
          else if (daysPastDue <= 60) b1.push(inv);
          else if (daysPastDue <= 90) b2.push(inv);
          else b3.push(inv);
        }
        const toAmount = (arr: (QuickbooksInvoice | any)[]) =>
          arr.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
        computedBuckets.push(
          { order: 0, invoices: b0, creditMemos: [], adjustedAmount: toAmount(b0) },
          { order: 1, invoices: b1, creditMemos: [], adjustedAmount: toAmount(b1) },
          { order: 2, invoices: b2, creditMemos: [], adjustedAmount: toAmount(b2) },
          { order: 3, invoices: b3, creditMemos: [], adjustedAmount: toAmount(b3) }
        );
      }

      // Attribute each credit to one bucket so clicking an aging category also selects the
      // credits behind that category's amount. Runs off the gross amounts, before the
      // job-filtered adjustment below consumes them.
      const creditAssignment = assignCreditsToAgingBuckets(
        openCreditMemos,
        computedBuckets.map((bucket) => ({ order: bucket.order, amount: bucket.adjustedAmount })),
        sortedRanges ?? DEFAULT_AGING_RANGES,
        currentSupplier?.arSettings?.agedCreditMemos === true
      );
      computedBuckets.forEach((bucket) => {
        bucket.creditMemos = creditAssignment.byBucketOrder[bucket.order] ?? [];
      });

      // Apply credits oldest-first (descending order) to adjust bucket amounts
      let adjustedCurrentDue = currentDueInvoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
      if (isJobFiltered && jobCreditTotal > 0) {
        let remaining = jobCreditTotal;
        const descBuckets = [...computedBuckets].sort((a, b) => b.order - a.order);
        for (const bucket of descBuckets) {
          if (remaining <= 0) break;
          const reduce = Math.min(remaining, Math.max(0, bucket.adjustedAmount));
          bucket.adjustedAmount -= reduce;
          remaining -= reduce;
        }
        adjustedCurrentDue = adjustedCurrentDue - remaining;
      }

      const totalAmountDue = isJobFiltered
        ? computedBuckets.reduce((sum, b) => sum + b.adjustedAmount, 0) + adjustedCurrentDue
        : documentsSummary?.totalAmountDueInvoices ?? 0;

      const totalCurrentDue = isJobFiltered
        ? adjustedCurrentDue
        : documentsSummary?.totalCurrentDueInvoices ?? 0;

      // Base summary items (always present)
      const baseSummary = [
        {
          key: 'summary_1',
          icon: (
            <div className="h-[24px] w-[24px] flex items-center justify-center mr-1 rounded-full bg-gray-50 border-gray-500 text-gray-700">
              <CurrencyCircleDollar size={20} />
            </div>
          ),
          value: formatMoney(totalAmountDue, 2),
          line1: 'AMOUNT',
          line2: 'DUE',
          invoices: [],
        },
        {
          key: 'summary_2',
          icon: (
            <div className="mr-1 h-[24px] w-[24px] flex items-center justify-center rounded-full bg-emerald-50 border-emerald-500 text-emerald-500">
              <Clock size={20} />
            </div>
          ),
          value: formatMoney(totalCurrentDue, 2),
          line1: 'CURRENT',
          line2: 'DUE',
          invoices: [...currentDueInvoices, ...creditAssignment.currentDue],
        },
      ];

      let agingSummary = [];

      if (isUsingAgingRanges && sortedRanges) {
        const bucketColors = [
          { bg: 'bg-orange-100', text: 'text-orange-700' },
          { bg: 'bg-red-100', text: 'text-red-700' },
          { bg: 'bg-rose-100', text: 'text-rose-700' },
        ];

        agingSummary = sortedRanges.map((range, index) => {
          const colorIndex = index % bucketColors.length;
          const colors = bucketColors[colorIndex];
          const displayLabel = range.label.toUpperCase();

          const bucketEntry = computedBuckets.find((b) => b.order === range.order);
          const serverBucket = Array.isArray(documentsSummary.agingBuckets)
            ? documentsSummary.agingBuckets.find((b) => b.order === range.order)
            : undefined;
          const amount = isJobFiltered
            ? bucketEntry?.adjustedAmount ?? 0
            : serverBucket?.amount ?? 0;

          return {
            key: `summary_aging_${range.order}`,
            icon: (
              <div
                className={`mr-2 text-[10px] ${colors.bg} ${colors.text} p-1 rounded-full h-[24px] w-[24px] flex items-center justify-center font-semibold`}>
                {range.max === null ? `>${range.min - 1}` : range.max}
              </div>
            ),
            value: formatMoney(amount, 2),
            line1: `${displayLabel} DAYS`,
            line2: 'PAST DUE',
            invoices: [...(bucketEntry?.invoices ?? []), ...(bucketEntry?.creditMemos ?? [])],
          };
        });
      } else {
        const [b0, b1, b2, b3] = computedBuckets;
        agingSummary = [
          {
            key: 'summary_3',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-red-50 border bg-amber-50 border-amber-500 text-amber-500 p-1 rounded-full font-semibold text-[10px]">
                30
              </div>
            ),
            value: formatMoney(
              isJobFiltered ? b0.adjustedAmount : documentsSummary?.totalPastDue1To30Invoices ?? '',
              2
            ),
            line1: '1 - 30 DAYS',
            line2: 'PAST DUE',
            invoices: [...b0.invoices, ...b0.creditMemos],
          },
          {
            key: 'summary_4',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-red-50 border bg-orange-50 border-orange-500 text-orange-500 p-1 rounded-full font-semibold text-[10px]">
                60
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b1.adjustedAmount
                : documentsSummary?.totalPastDue31To60Invoices ?? '',
              2
            ),
            line1: '31 - 60 DAYS',
            line2: 'PAST DUE',
            invoices: [...b1.invoices, ...b1.creditMemos],
          },
          {
            key: 'summary_5',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-red-50 border border-red-500 text-red-500 p-1 rounded-full font-semibold text-[10px]">
                90
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b2.adjustedAmount
                : documentsSummary?.totalPastDue61To90Invoices ?? '',
              2
            ),
            line1: '61 - 90 DAYS',
            line2: 'PAST DUE',
            invoices: [...b2.invoices, ...b2.creditMemos],
          },
          {
            key: 'summary_6',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-rose-50 border border-rose-600 text-rose-600 p-1 rounded-full font-semibold text-[10px]">
                {`>90`}
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b3.adjustedAmount
                : documentsSummary?.totalPastDueOver90Invoices ?? '',
              2
            ),
            line1: 'OVER 90 DAYS',
            line2: 'PAST DUE',
            invoices: [...b3.invoices, ...b3.creditMemos],
          },
        ];
      }

      return [...baseSummary, ...agingSummary];
    }
    return [];
  }, [
    documentsSummary,
    isUsingAgingRanges,
    buyerInvoices,
    buyerCreditMemos,
    selectedJobIds,
    currentSupplier?.arSettings?.agedCreditMemos,
  ]);

  const selectedJobs = useMemo<QuickbooksJob[]>(() => {
    if (!buyerInvoices || buyerInvoices.length === 0) return [];
    const jobs = buyerInvoices.reduce((prev: QuickbooksJob[], curr) => {
      if (curr.jobId && curr.quickBooksJob) return [...prev, curr.quickBooksJob];
      return prev;
    }, []);
    return removeDuplicatesByProperty({ inputArray: jobs, property: 'id' });
  }, [buyerInvoices]);

  const activeJobIds = useMemo(() => selectedJobIds.filter((id) => id !== 'ALL'), [selectedJobIds]);

  const activeJobs = useMemo(
    () => selectedJobs.filter((j) => activeJobIds.includes(j.id)),
    [selectedJobs, activeJobIds]
  );

  return (
    <div
      className={`w-full h-full p-4 ${
        activeMainTab === 'COLLECTIONS' ? 'overflow-y-auto' : ' flex flex-col overflow-hidden'
      }`}>
      {isSupplierCustomerPaymentMethodsModalOpen && (
        <SupplierCustomerPaymentMethodsModal
          open={isSupplierCustomerPaymentMethodsModalOpen}
          selectedCustomer={customerDetails}
          handleClose={() => {
            setIsSupplierCustomerPaymentMethodsModalOpen(false);
          }}
        />
      )}
      <CreditCardSettingsModal
        open={isPaymentSettingsModalOpen}
        selectedCustomer={selectedBuyer}
        handleClose={() => {
          setIsPaymentSettingsModalOpen(false);
          fetchBuyer(currentBuyerId);
        }}
      />

      <AccountAccessModal
        open={isAccountAccessModalOpen}
        buyerName={selectedBuyer?.name || ''}
        supplierId={currentSupplier?.id || ''}
        buyerId={currentBuyerId}
        usersEmailsDetails={customerDetails?.usersEmailsDetails || []}
        onRefresh={() => fetchBuyer(currentBuyerId)}
        onClose={() => setIsAccountAccessModalOpen(false)}
      />
      <CRMContactsModal
        open={isCRMContactsModalOpen}
        buyerName={selectedBuyer?.name || ''}
        contacts={selectedBuyer?.buyerContacts || []}
        onAddContact={handleAddBuyerContact}
        onDeleteContact={handleDeleteBuyerContact}
        onClose={() => setIsCRMContactsModalOpen(false)}
      />
      {isEmailPreferencesModalOpen && (
        <SupplierCustomerEmailPreferencesModal
          open={isEmailPreferencesModalOpen}
          buyer={selectedBuyer}
          initialUsers={customerDetails?.usersEmailsDetails || []}
          handleClose={(isUpdated) => {
            setIsEmailPreferencesModalOpen(false);
            if (isUpdated) fetchBuyer(currentBuyerId);
          }}
        />
      )}
      {creditManagerModalOpen && (
        <DillModal
          open
          onClose={() => setCreditManagerModalOpen(false)}
          title={currentCreditManager ? 'Change Credit Manager' : 'Assign Credit Manager'}
          subtitle={selectedBuyer?.name || ''}
          width="360px"
          footer={
            <>
              <DillButton
                variant="secondary"
                label="Cancel"
                onClick={() => setCreditManagerModalOpen(false)}
              />
              <DillButton
                label={savingCreditManager ? 'Saving…' : 'Save'}
                isLoading={savingCreditManager}
                onClick={handleSaveCreditManager}
              />
            </>
          }>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
              Select Credit Manager
            </label>
            <DillSelect
              value={selectedCreditManagerId}
              onChange={setSelectedCreditManagerId}
              placeholder="Select Credit Manager"
              options={[
                { value: '', label: '— No Credit Manager —' },
                ...creditManagers.map((cm) => ({
                  value: cm.id,
                  label: `${cm.firstName} ${cm.lastName}`,
                })),
              ]}
            />
          </div>
        </DillModal>
      )}
      {salesRepModalOpen && (
        <DillModal
          open
          onClose={() => setSalesRepModalOpen(false)}
          title={currentSalesRep ? 'Change Sales Rep' : 'Assign Sales Rep'}
          subtitle={selectedBuyer?.name || ''}
          width="360px"
          footer={
            <>
              <DillButton
                variant="secondary"
                label="Cancel"
                onClick={() => setSalesRepModalOpen(false)}
              />
              <DillButton
                label={savingSalesRep ? 'Saving…' : 'Save'}
                isLoading={savingSalesRep}
                onClick={handleSaveSalesRep}
              />
            </>
          }>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
              Select Sales Rep
            </label>
            <DillSelect
              value={selectedSalesRepId}
              onChange={setSelectedSalesRepId}
              placeholder="Select Sales Rep"
              options={[
                { value: '', label: '— No Sales Rep —' },
                ...salesReps.map((sp) => ({
                  value: sp.id,
                  label: sp.name,
                })),
              ]}
            />
          </div>
        </DillModal>
      )}

      <div className="w-full rounded-lg WHITE-BG px-4 py-2">
        <div className="w-full flex gap-4">
          <div className="w-full flex">
            {buyerInitial && (
              <div className="flex PRIMARY_100-BG PRIMARY_500-CLR h-[60px] w-[60px] text-base rounded-full items-center justify-center font-medium mr-2 flex-shrink-0">
                {buyerInitial}
              </div>
            )}
            <div className="w-full">
              <div className="flex flex-col mb-1 min-w-0">
                <div className="text-[20px] font-bold truncate">{selectedBuyer?.name ?? ''}</div>
                <div className="text-[14px] font-semibold truncate">
                  ({selectedBuyer?.quickbooksCustomerId ?? ''})
                </div>
              </div>

              <div className="flex flex-col gap-1">
                {selectedBuyer?.phoneNumbers && selectedBuyer?.phoneNumbers.length > 0 && (
                  <div className="flex items-center GREY_500-CLR font-semibold text-sm">
                    <Phone size={20} className="mr-2.5" />
                    <div className="text-xs">{selectedBuyer?.phoneNumbers?.join(', ') ?? ''}</div>
                  </div>
                )}
                {buyerAddress && (
                  <div className="flex items-center GREY_500-CLR font-semibold text-sm">
                    <MapPinLine size={20} className="mr-2" />
                    <div className="text-xs">{buyerAddress}</div>
                  </div>
                )}
              </div>
              {customerDetails?.usersEmailsDetails &&
                customerDetails?.usersEmailsDetails.length > 0 && (
                  <div className="flex min-h-[24px] text-xs mt-1 w-fit relative group font-semibold">
                    <div className="rounded-full GREY_500-CLR">
                      <Envelope size={20} className="mr-1" />
                    </div>
                    <div className="text-xs flex flex-wrap GREY_500-CLR">
                      {customerDetails?.usersEmailsDetails.slice(0, 2).map((item, i) => (
                        <div key={`${i}pp`} className="py-0.5 px-1 rounded mx-1 my-0.5">
                          {item.email}
                        </div>
                      ))}
                      {customerDetails?.usersEmailsDetails.length > 2 && (
                        <div className="py-0.5 px-1 rounded mx-1 my-0.5 GREY_100-BG ">
                          +{customerDetails?.usersEmailsDetails.length - 2} more
                        </div>
                      )}
                    </div>
                    {customerDetails?.usersEmailsDetails.length > 2 && (
                      <div className="absolute top-full left-0 mt-1 z-50 hidden group-hover:flex flex-wrap bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[200px] max-w-[360px]">
                        {customerDetails?.usersEmailsDetails.map((item, i) => (
                          <div
                            key={`${i}hover`}
                            className="py-0.5 px-2 rounded mx-1 my-0.5 text-xs  GREY_500-CLR font-semibold border border-gray-300">
                            {item.email}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
          <div className="flex w-full flex-col h-full px-4 gap-2">
            {selectedBuyer?.terms && (
              <div className="flex gap-2">
                <div className="text-xs font-semibold text-gray-500">Terms</div>
                <div className="text-xs font-medium">{selectedBuyer?.terms}</div>
              </div>
            )}
            <div className="flex gap-2 w-full justify-between h-full">
              <div className="flex gap-2 items-center">
                <div className="text-xs font-semibold text-gray-500">Credit Manager</div>
                {currentCreditManager ? (
                  <div className="text-xs font-medium flex items-center gap-1">
                    {currentCreditManager.firstName} {currentCreditManager.lastName}
                    <PencilSimple
                      color={COLORS.PRIMARY_500}
                      size={14}
                      className="cursor-pointer text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setSelectedCreditManagerId(selectedBuyer?.asignedCreditManagerId ?? '');
                        setCreditManagerModalOpen(true);
                      }}
                    />
                  </div>
                ) : (
                  <DillButton
                    label="Assign"
                    variant="secondary"
                    icon={<User size={14} />}
                    height={24}
                    onClick={() => {
                      setSelectedCreditManagerId('');
                      setCreditManagerModalOpen(true);
                    }}
                  />
                )}
              </div>
              <div className="flex gap-2 items-center">
                <div className="text-xs font-semibold text-gray-500">Sales Rep</div>
                {currentSalesRep ? (
                  <div className="text-xs font-medium flex items-center gap-1">
                    {currentSalesRep.name}
                    <PencilSimple
                      color={COLORS.PRIMARY_500}
                      size={14}
                      className="cursor-pointer text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setSelectedSalesRepId(selectedBuyer?.salesRepId ?? '');
                        setSalesRepModalOpen(true);
                      }}
                    />
                  </div>
                ) : (
                  <DillButton
                    label="Assign"
                    variant="secondary"
                    icon={<User size={14} />}
                    height={24}
                    onClick={() => {
                      setSelectedSalesRepId('');
                      setSalesRepModalOpen(true);
                    }}
                  />
                )}
              </div>
            </div>

            {selectedBuyer?.branch && (
              <div className="flex gap-2">
                <div className="text-xs font-semibold text-gray-500">Branch</div>
                <div className="text-xs font-medium">{selectedBuyer?.branch}</div>
              </div>
            )}

            {selectedBuyer?.isAutopayActive && (
              <div className="flex gap-2 items-center">
                <div className="text-xs font-semibold text-gray-500">Autopay</div>

                <>
                  <span className="inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 w-fit">
                    On
                  </span>
                  {(() => {
                    const details = selectedBuyer?.autopayDetails;
                    if (!details) return null;
                    const frequency = details.frequency ?? 'monthly';
                    const frequencyLabel =
                      frequencyOptions.find((o) => o.value === frequency)?.label ?? frequency;
                    const frequencyOnLabel = details.frequencyOn
                      ? onOptionsMap[frequency]?.find((o) => o.value === details.frequencyOn)
                          ?.label ?? details.frequencyOn
                      : '';
                    const strategy = details.paymentStrategy ?? 'ALL_OPEN';
                    const strategyLabel =
                      strategy === 'FLAT_AMOUNT'
                        ? `Flat ${formatMoney(Number(details.flatAmount ?? 0), 2)}`
                        : strategyOptions.find((o) => o.value === strategy)?.label ?? strategy;
                    return (
                      <div className="text-xs font-medium text-gray-700">
                        {frequencyLabel}
                        {frequencyOnLabel ? ` (${frequencyOnLabel})` : ''} · {strategyLabel}
                      </div>
                    );
                  })()}
                </>
              </div>
            )}

            {selectedBuyer?.creditAmount && (
              <div className="w-2/3">
                <div className="text-xs font-semibold text-gray-500 mb-1">Credit Utilization</div>
                {(() => {
                  const creditLimit = Number(selectedBuyer?.creditAmount ?? 0);
                  const totalAmount = Number(documentsSummary?.totalAmountDueInvoices ?? 0);
                  const creditUtilPct =
                    creditLimit > 0 ? Math.round((totalAmount / creditLimit) * 100) : 0;
                  const isOver = creditUtilPct > 100;
                  return (
                    <>
                      <div className="flex justify-between mb-0.5">
                        <span
                          className={`text-xs font-semibold ${
                            isOver ? 'text-red-600' : 'text-gray-700'
                          }`}>
                          {creditUtilPct}%
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatMoney(totalAmount, 2)} of {formatMoney(creditLimit, 0)}
                        </span>
                      </div>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-0">
                        <div
                          className={`h-full rounded-full ${
                            isOver ? 'bg-red-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(creditUtilPct, 100)}%` }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            <div className="flex flex-row gap-2 items-center ">
              {APP_ENVIRONMENT !== 'production' && (
                <DillButton
                  label={isCreditHoldDummy ? 'Remove Credit hold' : 'Put on Credit hold'}
                  variant="secondary"
                  height={24}
                  onClick={() => setIsCreditHoldDummy((prev) => !prev)}
                />
              )}
              {isCreditHoldDummy && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-4 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 w-fit">
                  <Warning size={12} weight="bold" />
                  Credit Hold
                </span>
              )}
            </div>
          </div>

          <div className="flex w-full flex-col">
            <div className="flex  items-center">
              {updatingLienStatus === selectedBuyer?.id ? (
                <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <DillToggle
                  value={selectedBuyer ? !selectedBuyer?.isPrelienNotificationsDisabled : false}
                  onChange={(val) =>
                    handleUpdateBuyerLienNotification(val, selectedBuyer?.id ?? '')
                  }
                />
              )}
              <div className="text-xs ml-2">
                Lien-related notifications to your team about this customer is enabled
              </div>
            </div>
            <div className="mt-3 flex gap-2 flex-wrap w-[500px]">
              <div>
                <DillButton
                  label="Account Access"
                  variant="secondary"
                  icon={<User size={18} />}
                  height={30}
                  onClick={() => {
                    setIsAccountAccessModalOpen(true);
                  }}
                />
              </div>
              <div>
                <DillButton
                  label="CRM Contacts"
                  variant="secondary"
                  icon={<AddressBook size={18} />}
                  height={30}
                  onClick={() => setIsCRMContactsModalOpen(true)}
                />
              </div>
              <div>
                <DillButton
                  label="View Payment Methods"
                  variant="secondary"
                  icon={<Cardholder size={18} />}
                  height={30}
                  onClick={() => {
                    setIsSupplierCustomerPaymentMethodsModalOpen(true);
                  }}
                />
              </div>
              <div>
                <DillButton
                  label="Payment Settings"
                  variant="secondary"
                  icon={<CreditCard size={18} />}
                  height={30}
                  onClick={() => {
                    setIsPaymentSettingsModalOpen(true);
                  }}
                />
              </div>
              <div>
                <DillButton
                  label="Notification Preferences"
                  variant="secondary"
                  icon={<Bell size={18} />}
                  height={30}
                  onClick={() => {
                    setIsEmailPreferencesModalOpen(true);
                  }}
                />
              </div>
              <div>
                <DillButton
                  label="Preview Portal"
                  variant="secondary"
                  icon={<Eye size={18} />}
                  height={30}
                  onClick={handlePreviewBuyerSide}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedJobs.length > 0 && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <div className="relative z-20">
            <DillDropdown
              inputFieldStyles={{ width: '160px', height: '36px', padding: '2px 2px' }}
              placeholder="Jobs"
              isSearchable={false}
              isCreatable={false}
              closeMenu={false}
              customViewElement={
                <div className="flex my-1 px-2 items-center">
                  <Buildings size={16} className="text-blue-500" />
                  <p className="text-sm ml-2 text-gray-600">Jobs Filter</p>
                </div>
              }
              itemWidth={300}
              items={[{ jobName: 'All Jobs', id: 'ALL' }, ...selectedJobs].map((job) => {
                const selectedJob = selectedJobs.find((j) => j.id === job.id);
                const jobAddress = `${selectedJob?.address ?? ''} ${selectedJob?.city ?? ''} ${
                  selectedJob?.state ? `${selectedJob?.state},` : ''
                } ${selectedJob?.zipCode ?? ''}`.trim();
                const jobName = `${selectedJob?.jobNumber ? `${selectedJob?.jobNumber} - ` : ''}${
                  job.jobName
                }`;
                return {
                  label: jobName,
                  value: job.id,
                  labelInElement: (
                    <div className="flex m-1 items-center gap-1">
                      <DillCheckbox isChecked={selectedJobIds?.includes(job.id)} />
                      <Buildings size={16} className="text-blue-500 flex-shrink-0" />
                      <div className="flex-1 truncate">
                        <div className="text-black text-xs truncate">{jobName}</div>
                        {jobAddress !== '' && (
                          <div className="text-gray-500 text-2xs truncate">{jobAddress}</div>
                        )}
                      </div>
                    </div>
                  ),
                };
              })}
              isMulti
              onMultiSelectChange={(data, oldMultiValue) => {
                dispatch(resetInvoiceIdsForDownload());
                dispatch(resetSelectInvoice());
                if (data) {
                  const valueList = data.map((item) => item.value);
                  const valueListWithoutAll = valueList.filter((item) => item !== 'ALL');
                  if (
                    !valueList.includes('ALL') &&
                    oldMultiValue &&
                    oldMultiValue.includes('ALL') &&
                    valueListWithoutAll.length === selectedJobs.length
                  ) {
                    setSelectedJobIds([]);
                  } else if (
                    valueList.includes('ALL') &&
                    valueListWithoutAll.length < selectedJobs.length &&
                    oldMultiValue &&
                    oldMultiValue.includes('ALL')
                  ) {
                    setSelectedJobIds(valueListWithoutAll);
                  } else if (
                    (valueList.includes('ALL') &&
                      valueListWithoutAll.length === 0 &&
                      oldMultiValue &&
                      !oldMultiValue.includes('ALL')) ||
                    valueList.includes('ALL') ||
                    (valueListWithoutAll.length === selectedJobs.length &&
                      !valueList.includes('ALL'))
                  ) {
                    setSelectedJobIds(['ALL', ...selectedJobs.map((item) => item.id)]);
                  } else if (valueList.length > 0) {
                    setSelectedJobIds(valueList);
                  } else {
                    setSelectedJobIds([]);
                  }
                }
              }}
              value={null}
              multiValue={selectedJobIds}
              menuAlign="left"
            />
            {activeJobIds.length > 0 && (
              <div className="flex absolute bottom-6 right-[-12px] bg-blue-500 text-white rounded-full text-xs font-bold h-5 w-5 items-center justify-center">
                {activeJobIds.length}
              </div>
            )}
          </div>
          {activeJobIds.length > 0 && (
            <>
              {activeJobs.slice(0, 2).map((job) => {
                const jobLabel = `${job.jobNumber ? `${job.jobNumber} - ` : ''}${job.jobName}`;
                return (
                  <span
                    key={job.id}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200 max-w-[200px]">
                    <Buildings size={10} className="flex-shrink-0" />
                    <span className="truncate">{jobLabel}</span>
                    <button
                      className="flex-shrink-0 flex items-center justify-center rounded-full hover:bg-red-200 p-0.5 cursor-pointer bg-transparent border-none text-red-500"
                      onClick={() => {
                        const next = selectedJobIds.filter((id) => id !== job.id && id !== 'ALL');
                        dispatch(resetInvoiceIdsForDownload());
                        dispatch(resetSelectInvoice());
                        setSelectedJobIds(next);
                      }}>
                      <X size={10} />
                    </button>
                  </span>
                );
              })}
              {activeJobs.length > 2 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200">
                  +{activeJobs.length - 2}
                </span>
              )}
              <button
                className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer bg-transparent border-none"
                onClick={() => {
                  setSelectedJobIds([]);
                  dispatch(resetInvoiceIdsForDownload());
                  dispatch(resetSelectInvoice());
                }}>
                Clear All
              </button>
            </>
          )}
        </div>
      )}
      <div className="flex w-full overflow-x-auto rounded-lg mt-2">
        {summaryDetails &&
          summaryDetails.length > 0 &&
          (() => {
            const currentDownloadIds = downloadIds?.OPEN || [];
            const currentSelectedCreditMemoIds = (selectedCreditMemosState?.OPEN || []).map(
              (creditMemo) => creditMemo.id
            );

            // A tile selects the invoices behind its amount plus the credits attributed to it.
            const isPendingTransactionOrReconciliation = (
              dillPaymentStatus: string | null | undefined
            ) =>
              dillPaymentStatus === 'TRANSACTION_PENDING' ||
              dillPaymentStatus === 'RECONCILIATION_PENDING';

            const splitSelectableDocs = (docs: (QuickbooksInvoice | QuickbooksCreditMemo)[]) => {
              const invoices: QuickbooksInvoice[] = [];
              const creditMemos: QuickbooksCreditMemo[] = [];
              docs.forEach((doc) => {
                const invoiceCheck = QuickbooksInvoiceSchema.safeParse(doc);
                if (invoiceCheck.success) {
                  if (!isPendingTransactionOrReconciliation(invoiceCheck.data.dillPaymentStatus)) {
                    invoices.push(invoiceCheck.data);
                  }
                  return;
                }
                const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(doc);
                if (
                  creditMemoCheck.success &&
                  !isPendingTransactionOrReconciliation(creditMemoCheck.data.dillPaymentStatus) &&
                  isCreditMemoSelectable(
                    creditMemoCheck.data.recordType,
                    currentSupplier?.integrationType
                  )
                ) {
                  creditMemos.push(creditMemoCheck.data);
                }
              });
              return { invoices, creditMemos };
            };

            const isDocSelected = (doc: QuickbooksInvoice | QuickbooksCreditMemo) =>
              currentDownloadIds.includes(doc.id) || currentSelectedCreditMemoIds.includes(doc.id);

            // Calculate if any bucket has selected invoices or credits
            const hasAnyBucketSelected = summaryDetails.some((summary) => {
              const { invoices, creditMemos } = splitSelectableDocs(summary.invoices);
              return [...invoices, ...creditMemos].some(isDocSelected);
            });

            return (
              <div className="w-full">
                <div className="flex w-full overflow-x-auto gap-4">
                  {summaryDetails.map((summary) => {
                    const hasInvoices = summary.invoices && summary.invoices.length > 0;

                    const { invoices: bucketInvoices, creditMemos: bucketCreditMemos } =
                      splitSelectableDocs(summary.invoices);
                    const selectableDocs = [...bucketInvoices, ...bucketCreditMemos];
                    const selectedDocsInBucket = selectableDocs.filter(isDocSelected);

                    const isAllSelectedForDownload =
                      selectableDocs.length > 0 &&
                      selectedDocsInBucket.length === selectableDocs.length;
                    const hasSomeSelectedForDownload = selectedDocsInBucket.length > 0;

                    return (
                      <div
                        key={summary.key}
                        className={twMerge(
                          'border border-gray-200 min-w-[160px] w-full WHITE-BG p-2 rounded-lg',
                          hasInvoices &&
                            'cursor-pointer hover:bg-blue-50 hover:border-1 hover:border-blue-500',
                          isAllSelectedForDownload && 'border-2 border-blue-500',
                          hasSomeSelectedForDownload &&
                            !isAllSelectedForDownload &&
                            'border-blue-300'
                        )}
                        onClick={() => {
                          if (!hasInvoices) return;

                          const invoiceIds = bucketInvoices.map((inv) => inv.id);

                          if (isAllSelectedForDownload) {
                            // Unselect all
                            dispatch(
                              unSelectInvoice({
                                tabId: 'OPEN',
                                invoices: bucketInvoices,
                              })
                            );
                            dispatch(
                              unSelectInvoiceIdsForDownload({
                                tabId: 'OPEN',
                                invoiceIds,
                              })
                            );
                            if (bucketCreditMemos.length > 0) {
                              dispatch(
                                unSelectCreditMemo({
                                  tabId: 'OPEN',
                                  creditMemos: bucketCreditMemos,
                                })
                              );
                            }
                          } else {
                            // Select all
                            dispatch(
                              selectInvoice({
                                tabId: 'OPEN',
                                invoices: bucketInvoices,
                              })
                            );
                            dispatch(
                              selectInvoiceIdsForDownload({
                                tabId: 'OPEN',
                                invoiceIds,
                              })
                            );
                            if (bucketCreditMemos.length > 0) {
                              dispatch(
                                selectCreditMemo({
                                  tabId: 'OPEN',
                                  creditMemos: bucketCreditMemos,
                                })
                              );
                            }
                          }
                        }}>
                        <div className="flex items-center mb-2 w-full">
                          {summary.icon}
                          <div className="font-bold text-[16px] ">{summary.value}</div>
                          {hasInvoices && (
                            <div className="ml-auto flex items-center">
                              {isAllSelectedForDownload ? (
                                <span className="text-xs text-blue-600 font-semibold">
                                  ✓ {selectableDocs.length}
                                </span>
                              ) : hasSomeSelectedForDownload ? (
                                <span className="text-xs text-gray-500">
                                  {selectedDocsInBucket.length}/{selectableDocs.length}
                                </span>
                              ) : (
                                <span className="text-xs text-blue-500">
                                  {selectableDocs.length}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs GREY_500-CLR font-medium tracking-wider">
                          {summary.line1}
                        </div>
                        <div className="text-xs GREY_500-CLR font-medium tracking-wider">
                          {summary.line2}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {hasAnyBucketSelected && (
                  <p className="text-xs text-blue-500 mt-1">Invoices selected for aging category</p>
                )}
              </div>
            );
          })()}
      </div>
      {/* Tabs Section */}
      <div
        className={`mb-0.5 mt-2 z-20 ${
          activeMainTab === 'COLLECTIONS' ? ' sticky top-[-16px]' : ' flex-shrink-0'
        }`}>
        <div className="flex gap-1 p-1 rounded-xl bg-gray-200">
          {[
            {
              id: 'DASHBOARD' as const,
              label: 'Dashboard',
              icon: <Graph size={14} />,
            },
            { id: 'INVOICES' as const, label: 'Invoices', icon: <FileText size={14} /> },
            { id: 'COLLECTIONS' as const, label: 'Collections', icon: <Archive size={14} /> },
            { id: 'PAYMENTS' as const, label: 'Payments', icon: <CreditCard size={14} /> },
            {
              id: 'PAYMENT_REQUESTS' as const,
              label: 'Payment Requests',
              icon: <PaperPlaneRight size={14} />,
            },
            { id: 'DISPUTES' as const, label: 'Disputes', icon: <HandFist size={14} /> },
            {
              id: 'PROMISE_TO_PAY' as const,
              label: 'Promise to Pay',
              icon: <Handshake size={14} />,
            },
            {
              id: 'PAYMENT_PLAN' as const,
              label: 'Payment Plans',
              icon: <Repeat size={14} />,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveMainTab(tab.id)}
              className={twMerge(
                'flex-1 flex items-center justify-center gap-1.5 h-[34px] text-xs font-medium rounded-lg border-none cursor-pointer transition-all',
                activeMainTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'bg-transparent text-gray-500 hover:text-gray-800'
              )}>
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className={`flex-1 ${activeMainTab === 'COLLECTIONS' ? '' : 'min-h-0 overflow-hidden'}`}>
        {activeMainTab === 'INVOICES' && (
          <CustomerInvoicesTable
            selectedBuyer={selectedBuyer}
            customerDetails={customerDetails}
            onInvoicesFetched={handleInvoicesFetched}
            onCustomerRefresh={() => fetchBuyer(currentBuyerId)}
            selectedJobIds={selectedJobIds}
          />
        )}
        {activeMainTab === 'COLLECTIONS' && (
          <div className="h-full mt-2 bg-white p-2 rounded-lg mb-2">
            <div className="rounded-xl border border-gray-200 mb-2">
              <button
                type="button"
                onClick={() => setIsWorkflowsExpanded((previous) => !previous)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left border-none bg-transparent cursor-pointer">
                <span className="text-gray-400">
                  {isWorkflowsExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                </span>
                <span className="text-blue-500">
                  <Lightning size={18} weight="duotone" />
                </span>
                <span className="font-semibold text-sm text-gray-800">
                  Collection Sequences & Triggers
                </span>
              </button>
              <div
                className={
                  isWorkflowsExpanded ? 'border-t border-gray-100 px-2 pt-2 pb-2' : 'hidden'
                }>
                {currentBuyerId &&
                  (hasAnyCollectionSequencesOrTriggers ? (
                    <>
                      <BuyerCollectionTriggerTargets
                        supplierId={currentSupplier?.id ?? ''}
                        buyerId={currentBuyerId}
                        buyerName={selectedBuyer?.name}
                      />
                      <BuyerCollectionSequenceEnrollments
                        supplierId={currentSupplier?.id ?? ''}
                        buyerId={currentBuyerId}
                        buyerName={selectedBuyer?.name}
                      />
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
                      <p className="text-xs text-gray-500 m-0">
                        No collection sequences or triggers have been set up yet.
                      </p>
                      <DillButton
                        label="Set up Collection Sequences & Triggers"
                        variant="secondary"
                        onClick={() =>
                          navigate(
                            buildSupplierPath(
                              currentSupplier?.id,
                              '/collectionSequencesAndTriggers'
                            )
                          )
                        }
                      />
                    </div>
                  ))}
              </div>
            </div>

            <CollectionActivityTimeline
              buyerId={currentBuyerId}
              buyer={selectedBuyer}
              filterMode="ALL"
              selectedJobIds={selectedJobIds}
              onViewPaymentDetails={(transactionId) =>
                navigate(
                  buildSupplierPath(
                    currentSupplier?.id,
                    `/customers/${currentBuyerId}?transactionId=${transactionId}`
                  )
                )
              }
              onViewPaymentRequest={(paymentRequestId) =>
                navigate(
                  buildSupplierPath(
                    currentSupplier?.id,
                    `/customers/${currentBuyerId}?tab=PAYMENT_REQUESTS&paymentRequestId=${paymentRequestId}`
                  )
                )
              }
              onViewDisputeDetails={(disputeId) =>
                navigate(
                  buildSupplierPath(
                    currentSupplier?.id,
                    `/customers/${currentBuyerId}?tab=DISPUTES&disputeId=${disputeId}`
                  )
                )
              }
              onViewPromiseToPay={(promiseToPayId) =>
                navigate(
                  buildSupplierPath(
                    currentSupplier?.id,
                    `/customers/${currentBuyerId}?tab=PROMISE_TO_PAY&promiseToPayId=${promiseToPayId}`
                  )
                )
              }
            />
          </div>
        )}
        {/* Payment tab */}
        {activeMainTab === 'PAYMENTS' && currentBuyerId && (
          <SupplierCustomerPaymentsTable buyerId={currentBuyerId} selectedJobIds={selectedJobIds} />
        )}
        {activeMainTab === 'PAYMENT_REQUESTS' && currentBuyerId && (
          <SupplierCustomerPaymentRequests buyerId={currentBuyerId} buyer={selectedBuyer} />
        )}
        {activeMainTab === 'DISPUTES' && currentBuyerId && (
          <SupplierCustomerDisputes buyerId={currentBuyerId} selectedJobIds={selectedJobIds} />
        )}
        {activeMainTab === 'PROMISE_TO_PAY' && currentBuyerId && (
          <SupplierCustomerPromiseToPays
            buyerId={currentBuyerId}
            contacts={selectedBuyer?.buyerContacts ?? []}
          />
        )}
        {activeMainTab === 'PAYMENT_PLAN' && currentBuyerId && (
          <SupplierCustomerPaymentPlan
            buyerId={currentBuyerId}
            buyerUsers={customerDetails?.usersEmailsDetails || []}
          />
        )}
        {activeMainTab === 'DASHBOARD' && (
          <SupplierCustomerDashboard
            supplierId={currentSupplier?.id ?? ''}
            buyerId={currentBuyerId}
          />
        )}
      </div>
      {(currentBuyerId === 'pbtRR9RMafrl8yKMi3mc' || currentBuyerId === 'fufsg6A08Pby86t0z5NX') &&
        APP_ENVIRONMENT !== 'production' && (
          <button
            className="group fixed bottom-6 right-6 z-50 flex items-center gap-0 hover:gap-2 h-[42px] px-3 hover:px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white border border-transparent text-sm font-medium cursor-pointer transition-all duration-300 ease-in-out overflow-hidden shadow-[0_4px_14px_rgba(234,179,8,0.5),0_6px_24px_rgba(0,0,0,0.3)]"
            onClick={() =>
              navigate(buildSupplierPath(currentSupplier?.id, '/creditApplications'), {
                state: { triggerRequestUpdate: true },
              })
            }>
            <Warning size={20} weight="bold" className="shrink-0" />
            <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-xs group-hover:opacity-100 transition-all duration-300 ease-in-out">
              Trigger another credit review
            </span>
          </button>
        )}
    </div>
  );
};
